import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostMeta, getPostBody, blogPosts } from './blog/posts';
import { FloatingFoodEmojis } from './blog/BlogVisuals';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const meta = slug ? getPostMeta(slug) : undefined;
  const relatedPosts = blogPosts.filter(post => post.slug !== slug).slice(0, 2);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (meta) {
      document.title = `${meta.title} | IntelliEats Blog`;
      let description = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!description) {
        description = document.createElement('meta');
        description.name = 'description';
        document.head.appendChild(description);
      }
      description.content = meta.excerpt;
    }
    // Google AdSense - inject into <head>
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const adsScript = document.createElement('script');
      adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3041826185319356';
      adsScript.async = true;
      adsScript.setAttribute('crossorigin', 'anonymous');
      document.head.appendChild(adsScript);
    }
  }, [meta]);
  if (!meta) {
    return <Navigate to="/blog" replace data-id="element-2408" data-mp="3kvuik16xdpt8" />;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2409" data-mp="3oyr522zl662g">
      
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2410" data-mp="3tx2ggi5j4oms">
        {/* Header */}
        <header className="mb-12 text-center" data-id="element-2411" data-mp="2kpvhrbcadfi">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2412" data-mp="319y41s43x80z">
            
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2413" data-mp="367gify0w8fgd" />
            Back to Blog
          </Link>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-2414" data-mp="dd9hif9vu1ns">
            {meta.title}
          </h1>
          <div className="flex items-center justify-center text-sm text-slate-500 space-x-4" data-id="element-2415" data-mp="m6uwnzpylkxq">
            <span data-id="element-2416" data-mp="i59ebyunoeg5">By IntelliEats Team</span>
            <span data-id="element-2417" data-mp="i6c4g4k5mcv0">•</span>
            <span className="flex items-center" data-id="element-2418" data-mp="i62nf6h37g1g">
              <Clock className="w-4 h-4 mr-1" data-id="element-2419" data-mp="3qkkr6vzzw9f9" />
              {meta.readTime}
            </span>
          </div>
        </header>

        <FloatingFoodEmojis category={meta.category} data-id="element-2420" data-mp="3lnb2xjoce3t0" />

        <div className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm italic leading-relaxed text-slate-600" data-id="element-2421" data-mp="3g4uyec4k46g">
          This guide is for educational purposes and does not replace medical or dietary advice. Food additives are evaluated individually, and ingredients approved by food-safety authorities are permitted for specific uses and levels. If you have allergies, medical conditions, or questions about your diet, consult a qualified healthcare professional.
        </div>

        {/* Body */}
        {getPostBody(meta.slug)}

        {/* Medical Disclaimer */}
        <div className="mt-16 bg-slate-50 border border-slate-200 rounded-2xl p-6" data-id="element-2422" data-mp="18f9sshg49hds">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2" data-id="element-2423" data-mp="18he95yv7c8uy">
            Medical Disclaimer
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0" data-id="element-2424" data-mp="213ffh9qqdgg9">
            The information in this article is provided for educational purposes
            only and is not intended as medical, nutritional, or dietary advice.
            Ingredients approved by regulatory authorities are considered safe
            when used within established limits. Always consult a qualified
            healthcare professional or registered dietitian for guidance
            tailored to your personal health needs. See our{' '}
            <Link to="/editorial-policy" className="text-brand-600 hover:text-brand-700 font-medium" data-id="element-2425" data-mp="2oul2c8v39902">
              
              Editorial Policy
            </Link>{' '}
            to learn how we research and review our content.
          </p>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && <div className="mt-16 pt-12 border-t border-slate-200" data-id="element-2426" data-mp="r7ak2s0o5ozr">
            <h2 className="text-2xl font-black text-slate-900 mb-8 text-center" data-id="element-2427" data-mp="m8qsoc3b3l00">
              Keep Reading
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-2428" data-mp="1jv6etr6oh6tw">
              {relatedPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left" data-id="element-2429" data-mp="1exo5hzor0pld">
              
                  <div className="relative h-40 overflow-hidden" data-id="element-2430" data-mp="22m79w44ogi86">
                    <img src={post.image} alt={post.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2431" data-mp="22ns7y3q7ajas" />
                
                    <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 select-none rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-black tracking-tight backdrop-blur-sm" data-id="element-2432" data-mp="257osj6o9z0um">
                  
                      <span className="text-slate-900/70" data-id="element-2433" data-mp="2f3i3ilmcimaa">Intelli</span>
                      <span className="text-brand-600/80" data-id="element-2434" data-mp="2k4xralfwr6wd">Eats</span>
                    </span>
                  </div>
                  <div className="p-6" data-id="element-2435" data-mp="2o4ybyntloebv">
                    <span className="px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase" data-id="element-2436" data-mp="2t4knmsts5mik">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-slate-900 mt-3 mb-3 leading-snug group-hover:text-brand-700 transition-colors line-clamp-2" data-id="element-2437" data-mp="32udytsq0fzma">
                      {post.title}
                    </h3>
                    <span className="inline-flex items-center text-sm font-semibold text-brand-600 group-hover:text-brand-700" data-id="element-2438" data-mp="3bukbjcdvwvke">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-2439" data-mp="1co7uhkx4ydv1" />
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>}
      </article>
    </motion.div>;
}